/**
 * CheckoutPage starts payment process and therefore it will get data from ListingPage
 * (booking dates, listing data, and all the other data that affects to booking decision).
 * This data is saved to Session Store which only exists while the browsing session exists -
 * e.g. tab is open. (Session Store is not related to session cookies.)
 */
import reduce from 'lodash/reduce';
import Decimal from 'decimal.js';

import { isAfterDate, subtractTime } from '../../util/dates';
import { types as sdkTypes } from '../../util/sdkLoader';
import { getProcess } from '../../transactions/transaction';

const { UUID, Money } = sdkTypes;

// Validate that given 'obj' has all the keys of defined by validPropTypes parameter
// and values must pass related test-value-format function.
const validateProperties = (obj, validPropTypes) => {
  return reduce(
    Object.entries(validPropTypes),
    (acc, [prop, fn]) => {
      if (Object.prototype.hasOwnProperty.call(obj, prop) && fn(obj[prop])) {
        return acc;
      }
      return false;
    },
    true
  );
};

// Validate content of booking dates object received from SessionStore
export const isValidBookingDates = bookingDates => {
  const props = {
    bookingStart: d => d instanceof Date,
    bookingEnd: d => d instanceof Date,
  };
  return validateProperties(bookingDates, props);
};

// Validate content of listing object received from SessionStore.
// Currently only id & attributes.price are needed.
export const isValidListing = listing => {
  const props = {
    id: id => id instanceof UUID,
    attributes: v => {
      return typeof v === 'object' && v.price instanceof Money;
    },
  };
  return validateProperties(listing, props);
};

/**
 * After JSON.parse, sdkTypes.reviver sometimes leaves listing.id / attributes.price as plain
 * objects. Full page loads (e.g. Auth0 return) rely on storedData(); without SDK instances
 * isValidListing fails and checkout redirects away.
 */
export const normalizeListingAfterSessionParse = listing => {
  if (!listing) {
    return listing;
  }

  const id =
    listing.id instanceof UUID
      ? listing.id
      : listing.id && typeof listing.id.uuid === 'string'
      ? new UUID(listing.id.uuid)
      : null;

  const rawPrice = listing.attributes && listing.attributes.price;
  const price =
    rawPrice instanceof Money
      ? rawPrice
      : rawPrice &&
        typeof rawPrice === 'object' &&
        typeof rawPrice.amount === 'number' &&
        typeof rawPrice.currency === 'string'
      ? new Money(rawPrice.amount, rawPrice.currency)
      : null;

  if (!id || !price) {
    return listing;
  }

  return {
    ...listing,
    id,
    attributes: {
      ...listing.attributes,
      price,
    },
  };
};

const normalizeTransactionAfterSessionParse = transaction => {
  if (!transaction || !transaction.id) {
    return transaction;
  }
  if (transaction.id instanceof UUID) {
    return transaction;
  }
  if (typeof transaction.id.uuid === 'string') {
    return {
      ...transaction,
      id: new UUID(transaction.id.uuid),
    };
  }
  return transaction;
};

// Validate content of an transaction received from SessionStore.
// An id is required and the last transition needs to be one of the known transitions
export const isValidTransaction = transaction => {
  // Reject any mock transaction IDs that might have been cached
  if (transaction && transaction.id && transaction.id.uuid) {
    const mockIds = ['conflict-resolved', 'speculative-conflict-resolved'];
    if (mockIds.includes(transaction.id.uuid)) {
      return false;
    }
  }
  
  let process = null;
  try {
    const processName = transaction?.attributes?.processName;
    process = getProcess(processName);
  } catch (e) {
    console.error(
      'Transaction, found from sessionStorage, was following unsupported transaction process.'
    );
    return false;
  }

  const props = {
    id: id => id instanceof UUID,
    type: type => type === 'transaction',
    attributes: v => {
      return typeof v === 'object' && Object.values(process.transitions).includes(v.lastTransition);
    },
  };
  return validateProperties(transaction, props);
};

// Stores given bookinData, listing and transaction to sessionStorage
export const storeData = (orderData, listing, transaction, storageKey) => {
  if (window && window.sessionStorage && listing && orderData) {
    const data = {
      orderData,
      listing,
      transaction,
      storedAt: new Date(),
    };

    const replacer = function(k, v) {
      if (this[k] instanceof Date) {
        return { date: v, _serializedType: 'SerializableDate' };
      }
      if (this[k] instanceof Decimal) {
        return { decimal: v, _serializedType: 'SerializableDecimal' };
      }
      return sdkTypes.replacer(k, v);
    };

    const storableData = JSON.stringify(data, replacer);
    window.sessionStorage.setItem(storageKey, storableData);
  }
};

// Get stored data
export const storedData = storageKey => {
  if (window && window.sessionStorage) {
    const checkoutPageData = window.sessionStorage.getItem(storageKey);

    const reviver = (k, v) => {
      if (v && typeof v === 'object' && v._serializedType === 'SerializableDate') {
        // Dates are expected to be stored as:
        // { date: new Date(), _serializedType: 'SerializableDate' }
        return new Date(v.date);
      } else if (v && typeof v === 'object' && v._serializedType === 'SerializableDecimal') {
        // Decimals are expected to be stored as:
        // { decimal: v, _serializedType: 'SerializableDecimal' }
        return new Decimal(v.decimal);
      }
      return sdkTypes.reviver(k, v);
    };

    // Note: orderData may contain bookingDates if booking process is used.
    let { orderData, listing, transaction, storedAt } = checkoutPageData
      ? JSON.parse(checkoutPageData, reviver)
      : {};

    listing = normalizeListingAfterSessionParse(listing);
    transaction = normalizeTransactionAfterSessionParse(transaction);

    const bookingDates = orderData?.bookingDates;
    const isPotentiallyIncludedBookingDatesValid = bookingDates
      ? isValidBookingDates(bookingDates)
      : true;

    // If sessionStore contains freshly saved data (max 1 day old), use it.
    // Missing storedAt: treat as usable when parse produced orderData+listing (legacy payloads).
    const isFreshlySaved = storedAt
      ? isAfterDate(storedAt, subtractTime(new Date(), 1, 'days'))
      : Boolean(checkoutPageData && orderData && listing);

    // resolve transaction as valid if it is missing
    const isTransactionValid = !!transaction ? isValidTransaction(transaction) : true;

    const listingAndMetaOk =
      isFreshlySaved &&
      isPotentiallyIncludedBookingDatesValid &&
      isValidListing(listing);

    if (listingAndMetaOk && isTransactionValid) {
      return { orderData, listing, transaction };
    }

    // After a deploy, process definitions can change; a cached lastTransition may no longer
    // be in the new transition set. Dropping the stale transaction still lets checkout
    // restore listing/orderData and speculate a fresh transaction (avoids redirect to /purchase).
    if (listingAndMetaOk && transaction && !isTransactionValid) {
      return { orderData, listing, transaction: null };
    }
  }
  return {};
};

/** Safe summary of sessionStorage checkout JSON for debugging (no secrets). */
export const getCheckoutSessionStorageSnapshot = storageKey => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return { storageKey, sessionStorageAvailable: false };
  }
  const raw = window.sessionStorage.getItem(storageKey);
  const base = {
    storageKey,
    sessionStorageAvailable: true,
    hasRawString: !!raw,
    rawCharLength: raw ? raw.length : 0,
    parseError: null,
    parsedTopLevelKeys: null,
    hasListingInParsed: false,
    hasOrderDataInParsed: false,
    listingIdUuid: null,
    storedAtPresent: false,
  };
  if (!raw) {
    return base;
  }
  try {
    const p = JSON.parse(raw);
    if (p && typeof p === 'object') {
      base.parsedTopLevelKeys = Object.keys(p);
      base.hasListingInParsed = !!p.listing;
      base.hasOrderDataInParsed = !!p.orderData;
      base.storedAtPresent = !!p.storedAt;
      const lid = p.listing && p.listing.id;
      base.listingIdUuid =
        typeof lid === 'object' && lid && typeof lid.uuid === 'string' ? lid.uuid : null;
    }
  } catch (e) {
    base.parseError = e instanceof Error ? e.message : 'parse failed';
  }
  return base;
};

export const clearData = storageKey => {
  if (window && window.sessionStorage) {
    window.sessionStorage.removeItem(storageKey);
  }
};

// Clear any cached mock transaction data
export const clearMockTransactionData = storageKey => {
  if (window && window.sessionStorage) {
    const storedData = window.sessionStorage.getItem(storageKey);
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        if (parsedData.transaction && parsedData.transaction.id && 
            (parsedData.transaction.id.uuid === 'conflict-resolved' || 
             parsedData.transaction.id.uuid === 'speculative-conflict-resolved')) {
          window.sessionStorage.removeItem(storageKey);
          return true;
        }
      } catch (e) {
        window.sessionStorage.removeItem(storageKey);
        return true;
      }
    }
  }
  return false;
};

/**
 * Save page data to sessionstorage if the data is passed through navigation
 *
 * @param {Object} pageData an object containing orderData, listing and transaction entities.
 * @param {String} storageKey key for the sessionStorage
 * @param {Object} history navigation related object with pushState action
 * @returns pageData
 */
export const handlePageData = ({ orderData, listing, transaction }, storageKey, history) => {
  // Browser's back navigation should not rewrite data in session store.
  // Action is 'POP' on both history.back() and page refresh cases.
  // Action is 'PUSH' when user has directed through a link
  // Action is 'REPLACE' when user has directed through login/signup process
  const hasNavigatedThroughLink = history.action === 'PUSH' || history.action === 'REPLACE';

  const hasDataInProps = !!(orderData && listing && hasNavigatedThroughLink);
  if (hasDataInProps) {
    // Store data only if data is passed through props and user has navigated through a link.
    storeData(orderData, listing, transaction, storageKey);
  }

  // NOTE: stored data can be empty if user has already successfully completed transaction.
  let pageData = hasDataInProps ? { orderData, listing, transaction } : storedData(storageKey);

  // On POP/refresh, sessionStorage may be missing or expired while CheckoutPage Redux still
  // holds listing/orderData from SET_INITIAL_VALUES — use that instead of losing checkout.
  if (!pageData?.listing && listing && orderData) {
    pageData = { orderData, listing, transaction };
  }

  return pageData;
};
